import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { ArrowLeft, BookOpen, Clock, Play, CheckCircle2, Circle, Loader2, Trash2, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import { useTheme } from '@/context/ThemeContext';

const API = `${window.location.origin}/api`;

const CourseDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const [course, setCourse] = useState(null);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSources, setShowSources] = useState(false);
  const touchStartX = React.useRef(0);
  const touchEndX = React.useRef(0);

  useEffect(() => {
    // Try to load from cache first for instant display
    const cachedCourse = sessionStorage.getItem(`course_${id}`);
    const cachedProgress = sessionStorage.getItem(`course_progress_${id}`);
    
    if (cachedCourse) {
      try {
        setCourse(JSON.parse(cachedCourse));
        setLoading(false);
      } catch (e) { /* ignore */ }
    }
    
    if (cachedProgress) {
      try {
        setProgress(JSON.parse(cachedProgress));
      } catch (e) { /* ignore */ }
    }
    
    // Load fresh data (will update cache)
    loadCourseData();
    
    // Add swipe gesture listener
    const handleTouchStart = (e) => {
      touchStartX.current = e.changedTouches[0].screenX;
    };
    
    const handleTouchEnd = (e) => {
      touchEndX.current = e.changedTouches[0].screenX;
      handleSwipe();
    };
    
    const handleSwipe = () => {
      // Swipe left (start > end) with at least 100px distance
      if (touchStartX.current - touchEndX.current > 100) {
        navigate('/courses');
      }
    };
    
    document.addEventListener('touchstart', handleTouchStart);
    document.addEventListener('touchend', handleTouchEnd);
    
    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [id, navigate]);

  const loadCourseData = async (retryCount = 0) => {
    try {
      const [courseRes, progressRes] = await Promise.all([
        axios.get(`${API}/courses/${id}`, { withCredentials: true }),
        axios.get(`${API}/progress/${id}`, { withCredentials: true })
      ]);
      
      // Cache the data
      sessionStorage.setItem(`course_${id}`, JSON.stringify(courseRes.data));
      sessionStorage.setItem(`course_progress_${id}`, JSON.stringify(progressRes.data));
      
      console.log('📚 Course loaded:', courseRes.data.title, '(', courseRes.data.complexity, ')');
      console.log('📖 Sources object:', courseRes.data.sources);
      console.log('   Primary sources:', courseRes.data.sources?.primary_sources?.length || 0);
      console.log('   Secondary sources:', courseRes.data.sources?.secondary_sources?.length || 0);
      console.log('   Recommended readings:', courseRes.data.sources?.recommended_readings?.length || 0);
      setCourse(courseRes.data);
      setProgress(progressRes.data);
      setLoading(false);
    } catch (error) {
      console.error('Failed to load course:', error);
      // Retry up to 3 times with a delay for newly generated courses
      if (retryCount < 3) {
        console.log(`Retrying course load... attempt ${retryCount + 2}`);
        setTimeout(() => loadCourseData(retryCount + 1), 1000);
      } else {
        setLoading(false);
      }
    }
  };

  const startLesson = (lessonNumber) => {
    navigate(`/learn/${id}/${lessonNumber}`);
  };

  const isLessonCompleted = (lessonNumber) => {
    return progress?.completed_lessons?.includes(lessonNumber);
  };

  const handleDeleteCourse = async () => {
    try {
      await axios.delete(`${API}/courses/${id}`, { withCredentials: true });
      navigate('/courses');
    } catch (error) {
      toast.error('Failed to delete course');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center theme-bg">
        <Loader2 className="w-12 h-12 animate-spin text-amber-600" />
      </div>
    );
  }

  if (!course) {
    return (
      <div className="min-h-screen flex items-center justify-center theme-bg">
        <Card className="card-glow p-8 text-center">
          <h2 className="text-2xl font-bold text-amber-900 mb-4">Unable to load course</h2>
          <p className="text-amber-700 mb-4">The course may still be generating or there was an issue loading it.</p>
          <Button onClick={() => navigate('/courses')} className="bg-amber-600 hover:bg-amber-700">
            Back to Courses
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen theme-bg">
      {/* Header */}
      <header className={`${isDarkMode ? 'bg-black/90 border-amber-900/50' : 'bg-white/80 border-amber-200'} backdrop-blur-sm border-b sticky top-0 z-50 shadow-sm`}>
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/courses')}
                className={`${isDarkMode ? 'text-amber-400' : 'text-amber-700 hover:bg-amber-100'} btn-glow rounded-lg`}
                data-testid="back-to-courses-btn"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center space-x-2">
                <BookOpen className={`w-6 h-6 ${isDarkMode ? 'text-amber-500' : 'text-amber-700'}`} />
                <span className={`text-xl font-bold ${isDarkMode ? 'text-amber-100' : 'text-amber-900'}`}>Course Details</span>
              </div>
            </div>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  size="sm"
                  className="bg-red-600 text-white hover:bg-red-700"
                  data-testid="delete-course-detail-btn"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Course
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete Course?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Are you sure you want to delete "{course?.title}"? This action cannot be undone and will also delete all your progress in this course.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleDeleteCourse}
                    className="bg-red-600 hover:bg-red-700"
                    data-testid="confirm-delete-detail-btn"
                  >
                    Delete
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        {/* Course Header */}
        <div className="bg-gradient-to-r from-amber-100 to-orange-100 rounded-2xl p-8 mb-8 card-glow" data-testid="course-header">
          <div className="max-w-3xl">
            <div className="flex items-center space-x-2 mb-4">
              <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                course.complexity === 'novice' ? 'bg-green-100 text-green-700' :
                course.complexity === 'proficient' ? 'bg-amber-100 text-amber-700' :
                'bg-purple-100 text-purple-700'
              }`}>
                {course.complexity.charAt(0).toUpperCase() + course.complexity.slice(1)}
              </span>
              <div className="flex items-center text-amber-700">
                <Clock className="w-4 h-4 mr-1" />
                <span className="text-sm">{course.total_hours} hours</span>
              </div>
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold text-amber-900 mb-4">{course.title}</h1>
            <p className="text-lg text-amber-800 mb-6">{course.description}</p>
            <div className="bg-white/80 backdrop-blur-sm rounded-xl p-4">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-amber-700">Your Progress</span>
                <span className="text-sm font-bold text-amber-900">{progress?.completion_percentage?.toFixed(0) || 0}%</span>
              </div>
              <Progress value={progress?.completion_percentage || 0} className="h-3" />
              <div className="flex justify-between mt-2 text-xs text-amber-600">
                <span>{progress?.completed_lessons?.length || 0} of {course.lessons.length} lessons completed</span>
              </div>
            </div>
          </div>
        </div>

        {/* Sources Section */}
        {course.sources && (course.sources.primary_sources?.length > 0 || course.sources.secondary_sources?.length > 0 || course.sources.recommended_readings?.length > 0) && (
          <Card 
            className="card-glow border-amber-200 mb-8 cursor-pointer"
            data-testid="sources-section"
            onClick={() => setShowSources(!showSources)}
          >
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className={`text-2xl ${isDarkMode ? 'text-amber-100' : 'text-amber-900'}`}>Course Sources</CardTitle>
                  <CardDescription className={isDarkMode ? 'text-amber-300' : 'text-amber-700'}>
                    Academic sources and references used in this course
                  </CardDescription>
                </div>
                
                {/* Mobile: Just arrow icon */}
                <div className={isDarkMode ? 'md:hidden text-amber-300' : 'md:hidden text-amber-700'}>
                  {showSources ? (
                    <ChevronUp className="w-5 h-5" />
                  ) : (
                    <ChevronDown className="w-5 h-5" />
                  )}
                </div>
                
                {/* Desktop: Button with text */}
                <div className={`hidden md:flex px-4 py-2 rounded-md border items-center ${isDarkMode ? 'border-amber-600 text-amber-300' : 'border-amber-300 text-amber-700'}`}>
                  {showSources ? (
                    <>
                      <ChevronUp className="w-4 h-4 mr-2" />
                      Hide Sources
                    </>
                  ) : (
                    <>
                      <ChevronDown className="w-4 h-4 mr-2" />
                      View Sources
                    </>
                  )}
                </div>
              </div>
            </CardHeader>
            {showSources && (
              <CardContent className="space-y-6">
              {course.sources.primary_sources && course.sources.primary_sources.length > 0 && (
                <div>
                  <h3 className={`text-lg font-bold mb-3 ${isDarkMode ? 'text-amber-100' : 'text-amber-900'}`}>Primary Sources</h3>
                  <ul className="space-y-2">
                    {course.sources.primary_sources.map((source, index) => (
                      <li key={index} className="flex items-start">
                        <span className={`mr-2 ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`}>•</span>
                        <a 
                          href={`https://scholar.google.com/scholar?q=${encodeURIComponent(source)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`${isDarkMode ? 'text-amber-300 hover:text-amber-100' : 'text-amber-800 hover:text-amber-600'} underline decoration-dotted hover:decoration-solid transition-colors`}
                        >
                          {source}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              {course.sources.secondary_sources && course.sources.secondary_sources.length > 0 && (
                <div>
                  <h3 className={`text-lg font-bold mb-3 ${isDarkMode ? 'text-amber-100' : 'text-amber-900'}`}>Secondary Sources</h3>
                  <ul className="space-y-2">
                    {course.sources.secondary_sources.map((source, index) => (
                      <li key={index} className="flex items-start">
                        <span className={`mr-2 ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`}>•</span>
                        <a 
                          href={`https://scholar.google.com/scholar?q=${encodeURIComponent(source)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`${isDarkMode ? 'text-amber-300 hover:text-amber-100' : 'text-amber-800 hover:text-amber-600'} underline decoration-dotted hover:decoration-solid transition-colors`}
                        >
                          {source}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              {course.sources.recommended_readings && course.sources.recommended_readings.length > 0 && (
                <div>
                  <h3 className={`text-lg font-bold mb-3 ${isDarkMode ? 'text-amber-100' : 'text-amber-900'}`}>Recommended Readings</h3>
                  <ul className="space-y-2">
                    {course.sources.recommended_readings.map((source, index) => (
                      <li key={index} className="flex items-start">
                        <span className={`mr-2 ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`}>•</span>
                        <a 
                          href={`https://scholar.google.com/scholar?q=${encodeURIComponent(source)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`${isDarkMode ? 'text-amber-300 hover:text-amber-100' : 'text-amber-800 hover:text-amber-600'} underline decoration-dotted hover:decoration-solid transition-colors`}
                        >
                          {source}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              </CardContent>
            )}
          </Card>
        )}

        {/* Lessons List */}
        <Card className="card-glow border-amber-200 shadow-lg" data-testid="lessons-container">
          <CardHeader>
            <CardTitle className={`text-2xl ${isDarkMode ? 'text-amber-100' : 'text-amber-900'}`}>Course Curriculum</CardTitle>
            <CardDescription className={isDarkMode ? 'text-amber-300' : 'text-amber-700'}>
              {course.lessons.length} comprehensive lessons to master {course.topic}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Accordion type="single" collapsible className="space-y-4">
              {course.lessons.map((lesson, index) => {
                const completed = isLessonCompleted(lesson.lesson_number);
                const isCurrent = progress?.current_lesson === lesson.lesson_number;

                return (
                  <AccordionItem
                    key={lesson.lesson_number}
                    value={`lesson-${lesson.lesson_number}`}
                    className="border border-amber-200 rounded-lg overflow-hidden bg-white"
                    data-testid={`lesson-item-${lesson.lesson_number}`}
                  >
                    <AccordionTrigger className="px-6 py-4 hover:bg-amber-50">
                      <div className="flex items-center space-x-4 w-full">
                        <div className="flex-shrink-0">
                          {completed ? (
                            <CheckCircle2 className="w-6 h-6 text-green-600" data-testid={`lesson-${lesson.lesson_number}-completed-icon`} />
                          ) : isCurrent ? (
                            <Play className="w-6 h-6 text-amber-600" data-testid={`lesson-${lesson.lesson_number}-current-icon`} />
                          ) : (
                            <Circle className="w-6 h-6 text-amber-300" />
                          )}
                        </div>
                        <div className="flex-1 text-left">
                          <div className="flex items-center space-x-3">
                            <div className="font-semibold text-amber-900">Lesson {lesson.lesson_number}: {lesson.title}</div>
                            {progress?.quiz_scores?.[`lesson_${lesson.lesson_number}`] !== undefined && (
                              <span className={`px-2 py-1 text-xs font-semibold rounded ${
                                progress.quiz_scores[`lesson_${lesson.lesson_number}`] >= 70
                                  ? 'bg-green-100 text-green-700'
                                  : 'bg-yellow-100 text-yellow-700'
                              }`} data-testid={`lesson-${lesson.lesson_number}-score`}>
                                Final Quiz: {progress.quiz_scores[`lesson_${lesson.lesson_number}`]}%
                              </span>
                            )}
                          </div>
                          <div className="text-sm mt-1 text-amber-600">
                            {lesson.duration_minutes} minutes
                          </div>
                        </div>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-6 py-4 bg-amber-50/50">
                      <div className="space-y-4">
                        <div>
                          <h4 className="font-semibold mb-2 text-amber-900">Learning Objectives</h4>
                          <ul className="space-y-1 text-amber-700">
                            {lesson.objectives?.map((obj, i) => (
                              <li key={i} className="flex items-start">
                                <span className="mr-2">•</span>
                                <span>{obj}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                        <div>
                          <h4 className="font-semibold mb-2 text-amber-900">Key Topics</h4>
                          <div className="flex flex-wrap gap-2">
                            {lesson.key_terms?.map((term, i) => (
                              <span key={i} className="px-2 py-1 bg-amber-100 text-amber-700 rounded text-sm">
                                {term}
                              </span>
                            ))}
                          </div>
                        </div>
                        <Button
                          onClick={() => startLesson(lesson.lesson_number)}
                          className="bg-amber-600 hover:bg-amber-700 mt-4"
                          data-testid={`start-lesson-${lesson.lesson_number}-btn`}
                        >
                          {completed ? 'Review Lesson' : isCurrent ? 'Continue' : 'Start Lesson'}
                        </Button>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default CourseDetail;